/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useState } from "react"
import { observer } from "mobx-react-lite"
import { Button } from "@mui/material"
import { useBookingStores } from "../booking-context"
import WizardStepLayout from "../components/wizard-step-layout"
import ConsentList, { ConsentItem } from "../components/consent-list"
import { WizardConsents } from "../stores/wizard-store"
import bookingCopy from "../bookingCopy"
import { bookingColors, footerButtonSx } from "../booking-theme"

const copy = bookingCopy.consent

// The link's visible text is DERIVED from the one configured URL — never a second literal
// that could drift from the href (bookingCopy is the single source).
const printableFormLabel = copy.fullFormUrl.replace(/^https?:\/\//, "")

/** The full Medaica Consent Form, rendered inline (canonical, version-frozen text —
 * the medaica.com page linked at the end is the printable copy, not the source).
 * A named <section>, height-capped so the step's buttons stay reachable — which makes
 * it a scroll container, and a scroll container must be focusable (see below). */
const ConsentDocument = (): ReactElement => (
  <section
    id="consent-full-form"
    aria-labelledby="consent-full-form-title"
    // Required, not incidental: a height-capped scrollable region has to be keyboard
    // operable (WCAG 2.1.1 / axe scrollable-region-focusable) or a keyboard user can
    // never page through the document. "tabIndex on a non-interactive element" is a
    // false positive for scroll containers — keep the attribute.
    tabIndex={0} // NOSONAR
    className="mt-3 ml-0 max-h-96 overflow-y-auto rounded-lg border p-4 text-sm text-gray-700 sm:ml-8"
    style={{ backgroundColor: "#FAFAFE", borderColor: "#E6E6FA" }}
  >
    <h2 id="consent-full-form-title" className="text-base font-bold text-gray-900">
      {copy.documentTitle}
    </h2>
    {copy.document.map((section, index) => (
      <div key={section.heading ?? index}>
        {section.heading && <h3 className="mt-4 font-semibold text-gray-900">{section.heading}</h3>}
        {(section.paragraphs ?? []).map((paragraph) => (
          <p key={paragraph} className="mt-2">
            {paragraph}
          </p>
        ))}
        {section.bullets && (
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {section.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        )}
        {(section.after ?? []).map((paragraph) => (
          <p key={paragraph} className="mt-2">
            {paragraph}
          </p>
        ))}
      </div>
    ))}
    <p className="mt-4 text-gray-500">
      {copy.fullFormAlso}{" "}
      <a
        href={copy.fullFormUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all font-medium hover:underline"
        style={{ color: bookingColors.primary }}
      >
        {printableFormLabel}
      </a>
      {"."}
    </p>
  </section>
)

const Consent = observer((): ReactElement => {
  const { uiStore, wizardStore } = useBookingStores()
  const [showFullForm, setShowFullForm] = useState(false)
  const patient = wizardStore.patient
  const patientName = patient ? `${patient.firstName} ${patient.lastName}`.trim() : ""

  // Rendered as the row's `action` — a sibling of the text label, never inside it.
  const viewFullFormToggle = (
    <button
      type="button"
      aria-expanded={showFullForm}
      aria-controls="consent-full-form"
      className="font-semibold hover:underline"
      style={{ color: bookingColors.primary }}
      onClick={() => setShowFullForm((current) => !current)}
    >
      {showFullForm ? copy.hideFullForm : copy.viewFullForm} {showFullForm ? "▴" : "▾"}
    </button>
  )

  const items: ConsentItem[] = [
    {
      key: "telehealth", // the consent TYPE the backend stores; its text is policy v1
      title: copy.formTitle,
      body: copy.formBody,
      action: viewFullFormToggle,
      expansion: showFullForm ? <ConsentDocument /> : null,
    },
    { key: "privacyNotice", title: copy.privacyTitle, body: copy.privacyBody },
    { key: "appointmentEmails", title: copy.emailsTitle, body: copy.emailsBody(patient?.email ?? "") },
  ]

  const subtitle =
    wizardStore.forWhom === "me" ? copy.subtitleForSelf : copy.subtitleForOther(patient?.firstName ?? "them")

  const handleToggle = (key: keyof WizardConsents, checked: boolean): void => {
    wizardStore.setConsent(key, checked)
  }

  return (
    <WizardStepLayout current="consent" headerRight={bookingCopy.bookingFor(patientName)}>
      <h1 className="text-2xl font-bold" style={{ color: bookingColors.primary }}>
        {copy.title}
      </h1>
      <p className="mt-1 mb-6 text-gray-600">{subtitle}</p>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <ConsentList items={items} values={wizardStore.consents} onToggle={handleToggle} />
      </div>

      {/* Quiet legal note — flat light-indigo panel (portal's blue.light.1 tint) + hairline border,
          no shadow, so it reads as meta info rather than another content card. */}
      <div
        className="mt-4 rounded-lg border px-6 py-4 text-sm text-gray-500"
        style={{ backgroundColor: "#F6F6FB", borderColor: "#E6E6FA" }}
      >
        {copy.footnote}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outlined" color="inherit" sx={footerButtonSx} onClick={() => uiStore.goTo("patient")}>
          {copy.back}
        </Button>
        <Button
          variant="contained"
          sx={footerButtonSx}
          disabled={!wizardStore.allConsentsGranted}
          onClick={() => uiStore.goTo("health")}
        >
          {copy.agree}
        </Button>
      </div>
    </WizardStepLayout>
  )
})

export default Consent
